/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * An error returned from the server.  Each endpoint has a set of endpoint-specific error codes.
 *
 * @param errorCode An error code that is specific to a given endpoint.
 * @param errorMessage A non-localized error message.  This should not be displayed to users.  It is provided to assist
 * in debugging beyond what can be provided with the [errorCode]
 */
@Serializable
data class ServerError(
    @SerialName("error_code") val errorCode: String,
    @SerialName("error_message") val errorMessage: String
)

object CommonErrorCodes {
    /**
     * Returned when the authentication is invalid.
     */
    const val UNAUTHORIZED = "UNAUTHORIZED"

    /**
     * Returned when the JSON object from the initial request could not be parsed by the server.
     */
    const val FAILED_PARSING_JSON = "FAILED_PARSING_JSON"

    /**
     * The server returned an error code that the client does not know about, so the actual error code was remapped to
     * this constant for type safety within the client.
     *
     * To resolve this error, clients should update to the latest SDK version.
     */
    const val UNKNOWN = "UNKNOWN_ERROR"

    /**
     * The server returned an error HTTP status code, but the error JSON object was missing.
     *
     * This most likely occurs when the error is generated by a proxy of the server, such as a Content Delivery Network,
     * which does not implement the server's endpoint APIs.
     */
    const val PROXY = "PROXY_ERROR"
}

object LoginErrorCodes {
    /**
     * @see CommonErrorCodes.UNAUTHORIZED
     */
    const val UNAUTHORIZED = CommonErrorCodes.UNAUTHORIZED

    /**
     * @see CommonErrorCodes.FAILED_PARSING_JSON
     */
    const val FAILED_PARSING_JSON = CommonErrorCodes.FAILED_PARSING_JSON

    /**
     * @see CommonErrorCodes.UNKNOWN
     */
    const val UNKNOWN = CommonErrorCodes.UNKNOWN

    /**
     * @see CommonErrorCodes.PROXY
     */
    const val PROXY = CommonErrorCodes.PROXY
}

object AssessmentErrorCodes {
    /**
     * @see CommonErrorCodes.FAILED_PARSING_JSON
     */
    const val FAILED_PARSING_JSON = CommonErrorCodes.FAILED_PARSING_JSON

    /**
     * @see CommonErrorCodes.UNKNOWN
     */
    const val UNKNOWN = CommonErrorCodes.UNKNOWN

    /**
     * @see CommonErrorCodes.PROXY
     */
    const val PROXY = CommonErrorCodes.PROXY

    /**
     * Returned when the user does not exist.  This could happen if the user ID is not correct.
     */
    const val USER_NOT_FOUND = "USER_NOT_FOUND"

    /**
     * Returned when creating the assessment failed for some unknown reason, usually due to a database constraint failure.
     */
    const val ASSESSMENT_NOT_CREATED = "ASSESSMENT_COULD_NOT_BE_CREATED"
}

object UserErrorCodes {
    /**
     * @see CommonErrorCodes.FAILED_PARSING_JSON
     */
    const val FAILED_PARSING_JSON = CommonErrorCodes.FAILED_PARSING_JSON

    /**
     * @see CommonErrorCodes.UNKNOWN
     */
    const val UNKNOWN_ERROR = CommonErrorCodes.UNKNOWN

    /**
     * @see CommonErrorCodes.PROXY
     */
    const val PROXY_ERROR = CommonErrorCodes.PROXY

    /**
     * Returned when the user is a duplicate, as determined from the email address.
     *
     * To resolve this issue either:
     *  - Sign in with the email address instead of trying to create a new user
     *  - Use a different email address to create a new user
     */
    const val USER_ALREADY_EXISTS = "USER_ALREADY_EXISTS"

    /**
     * Returned when creating the user failed for some unknown reason.
     */
    const val USER_COULD_NOT_BE_CREATED = "USER_COULD_NOT_BE_CREATED"
}

object SessionErrorCodes {
    /**
     * Returned when creating the token failed for some unknown reason.
     */
    const val TOKEN_COULD_NOT_BE_CREATED = "TOKEN_COULD_NOT_BE_CREATED"
}

object AnswerErrorCodes {
    /**
     * @see CommonErrorCodes.FAILED_PARSING_JSON
     */
    const val FAILED_PARSING_JSON = CommonErrorCodes.FAILED_PARSING_JSON

    /**
     * There is a mismatch between the answer and the question, such as there is no question with the given ID, the
     * answer value doesn't match the question type, or the answer is out of range for the question.
     */
    const val ANSWER_DOES_NOT_MATCH_QUESTION = "ANSWER_DOES_NOT_MATCH_QUESTION"
}
